import React, { useEffect, useState } from "react";
import {
  Modal,
  Fade,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Grid,
  Paper,
  Box,
  Alert,
} from "@mui/material";
import { useSelector } from "react-redux";
import ContactsTab from "./ContactsTab";
import AddressesTab from "./AddressesTab";
import AccountNotesTab from "./AccountNotesTab";
import ThemedTextField from "../components/ThemedTextField";
import ThemedSelect from "../components/ThemedSelect";
import ThemedAutocomplete from "../components/ThemedAutocomplete";
import ThemedTypography from "../components/ThemedTypography";
import MobileFileList from "../components/MobileFileList";
import {
  PROJECT_STATUSES,
  DARK_THEME_BODY_COLOR,
  DARK_THEME_INPUT_OUTLINE_COLOR,
} from "../constants";
import { decoratedFetch } from "../req_utils";
import {
  createAutogeneratedLog,
  createProjectAssignmentLog,
} from "../log_utils";

const MobileCreateProjectView = ({
  createClicked,
  setCreateClicked,
  setCreateLoading,
  onCreateProject,
  employees,
  setSnackbarMessage,
}) => {
  const darkThemeEnabled = useSelector((state) => state.darkThemeEnabled);
  const employee = useSelector((state) => state.employee);
  const [account, setAccount] = useState("new_account");
  const [accounts, setAccounts] = useState([]);
  const [accountName, setAccountName] = useState("");
  const [accountType, setAccountType] = useState("");
  const [accountTypes, setAccountTypes] = useState([]);
  const [projectName, setProjectName] = useState("");
  const [addressLn1, setAddressLn1] = useState("");
  const [addressLn2, setAddressLn2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [salesEmployee, setSalesEmployee] = useState("");
  const [alertMessage, setAlertMessage] = useState("");

  const handleSalesEmployeeChange = (event) =>
    setSalesEmployee(event.target.value);
  const handleAccountNameChange = (event) => setAccountName(event.target.value);
  const handleProjectNameChange = (event) => setProjectName(event.target.value);
  const handleAddressLn1Change = (event) => setAddressLn1(event.target.value);
  const handleAddressLn2Change = (event) => setAddressLn2(event.target.value);
  const handleCityChange = (event) => setCity(event.target.value);
  const handleStateChange = (event) => setState(event.target.value);
  const handleZipChange = (event) => setZip(event.target.value);
  const handleAccountTypeChange = (event) => setAccountType(event.target.value);

  useEffect(() => {
    if (createClicked) {
      setCreateClicked(false);
      handleSend();
    }
  }, [createClicked]);

  useEffect(() => {
    if (!employee.branch_id) return;
    
    decoratedFetch(`/list_accounts?branch_id=${employee.branch_id}`)
      .then((response) => response.json())
      .then((data) => {
        // Check if 'data' is an array before updating state
        if (Array.isArray(data)) {
          setAccounts(data);
        } else {
          console.error("Expected an array but got:", data);
          setAccounts([]); // Set to an empty array or handle as appropriate
        }
      })
      .catch((error) => {
        console.error("Error fetching data: ", error);
        setAccounts([]); // Set to an empty array in case of error
      });
    decoratedFetch("/list_account_types")
      .then((response) => response.json())
      .then((data) => {
        // Assuming 'data' should be an array of contact types, check if it's actually an array
        if (Array.isArray(data)) {
          setAccountTypes(data);
        } else {
          console.error("Expected an array of contact types, received:", data);
          setAccountTypes([]); // Set to an empty array if the response is not as expected
        }
      })
      .catch((error) => {
        console.error("Error fetching contact types:", error);
        setAccountTypes([]); // Set to an empty array in case of an error
      });
  }, []);

  const handleSend = () => {
    if (!projectName || !salesEmployee) {
      setAlertMessage("Missing required project fields");
      setCreateLoading(false);
      return;
    }

    let payload;
    if (account === "new_account") {
      if (!accountName || !accountType) {
        setAlertMessage("Missing required account fields");
        setCreateLoading(false);
        return;
      }

      payload = {
        name: projectName,
        sales_employee: salesEmployee,
        branch_id: employee.branch_id,
        account: {
          name: accountName,
          account_type_id: accountType,
        },
      };
    } else {
      payload = {
        account_id: account?.id,
        name: projectName,
        sales_employee: salesEmployee,
        branch_id: employee.branch_id,
      };
    }

    decoratedFetch("/create_project", {
      method: "POST",
      body: JSON.stringify(payload),
    })
      .then((response) => {
        if (response.status === 201) {
          return response.json();
        }
        throw new Error();
      })
      .then((data) => {
        setCreateLoading(false);
        onCreateProject(data.project_id);
        setSnackbarMessage("Project created");
      })
      .catch((error) => {
        console.error("Error creating project:", error);
        setSnackbarMessage("Failed to create project");
        setCreateLoading(false);
      });
  };

  const handleAlertClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setAlertMessage("");
  };

  return (
    <Grid container spacing={2}>
      {alertMessage && (
        <Grid
          item
          xs={12}
          sx={{
            "& .MuiPaper-root": {
              mb: 0,
            },
            mt: "15px",
            mx: "15px",
          }}
        >
          <Alert
            onClose={handleAlertClose}
            severity="error"
            sx={{
              width: "100%",
              mb: 2,
            }}
          >
            {alertMessage}
          </Alert>
        </Grid>
      )}
      <Grid item xs={12} sx={{ mx: "15px", mt: alertMessage ? 0 : "15px" }}>
        <ThemedTextField
          label="Project Name"
          type="project name"
          size="small"
          fullWidth
          variant="outlined"
          value={projectName}
          onChange={handleProjectNameChange}
        />
      </Grid>
      <Grid item xs={12} sx={{ mx: "15px" }}>
        <ThemedSelect
          id="sales-select"
          size="small"
          value={salesEmployee}
          onChange={handleSalesEmployeeChange}
          label="Sales Rep"
          fullWidth
          MenuProps={{
            disableScrollLock: true,
            PaperProps: {
              style: {
                maxHeight: "300px",
                overflowY: "auto",
              },
            },
          }}
        >
          {employees.map((employee) => (
            <MenuItem key={employee.id} value={employee}>
              {employee.first_name} {employee.last_name}
            </MenuItem>
          ))}
        </ThemedSelect>
      </Grid>
      <Grid item xs={12} sx={{ mx: "15px" }}>
        <ThemedAutocomplete
          size="small"
          options={accounts}
          getOptionLabel={(account) => account?.name || ""}
          value={account}
          onChange={(event, newValue) => {
            setAccount(newValue || "new_account");
          }}
          renderInput={(params) => (
            <ThemedTextField
              {...params}
              label="Existing Account"
              variant="outlined"
              fullWidth
              placeholder="Select Existing Account"
            />
          )}
        />
      </Grid>
      {account === "new_account" && (
        <Grid item xs={12} sx={{ mx: "15px" }}>
          <ThemedTypography variant="h7">New Account</ThemedTypography>
          <Grid container spacing={2}>
            <Grid item xs={12} sx={{ mt: "15px" }}>
              <ThemedSelect
                size="small"
                value={accountType}
                label="Account Type"
                onChange={handleAccountTypeChange}
                required
              >
                {accountTypes.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.name}
                  </MenuItem>
                ))}
              </ThemedSelect>
            </Grid>
            <Grid item xs={12}>
              <ThemedTextField
                label="Account Name"
                size="small"
                type="account name"
                fullWidth
                variant="outlined"
                value={accountName}
                onChange={handleAccountNameChange}
              />
            </Grid>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export default MobileCreateProjectView;
